import { oc } from "@orpc/contract";
import { z } from "zod";
import { Cents } from "./money";

/**
 * Pricing's admin API (CONTEXT.md): customer groups with their default discount, and the shop-wide
 * quantity discount list (Instellingen → Klantgroepen). A product's own group discounts and
 * quantity discounts are edited in the product editor (`ProductContract`), whose `ProductEditor`
 * carries them as `pricing` (`ProductPricing` below).
 *
 * Like the product editor, the settings page loads `listCustomerGroups` and replaces what it shows
 * with the `CustomerGroupSettings` every write answers. Errors come back as `{ _tag, message }`
 * with an HTTP status; the tags are named on each procedure. An invalid body answers 400.
 */

// ---------------------------------------------------------------------------------------------
// Fields

/** An integer percentage from 0 to 100: a customer group's discount. */
export const DiscountPercent = z.number().int().min(0).max(100);

/** The smallest quantity a step can start at: one piece is the plain price, so a staffel starts at 2. */
export const MIN_QUANTITY_DISCOUNT_PIECES = 2;

/** The most steps one quantity discount list has. */
export const MAX_QUANTITY_DISCOUNT_STEPS = 15;

/** "From `minQuantity` pieces, `percent` off": one step of a quantity discount list. */
export const QuantityDiscount = z.object({
	minQuantity: z.number().int().min(MIN_QUANTITY_DISCOUNT_PIECES).max(1_000_000),
	percent: z.number().int().min(1).max(99),
});
export type QuantityDiscount = z.infer<typeof QuantityDiscount>;

/** A quantity discount list ("staffel"): ascending `minQuantity`, each at most once. */
export const QuantityDiscountList = z
	.array(QuantityDiscount)
	.max(MAX_QUANTITY_DISCOUNT_STEPS)
	.refine(
		(list) =>
			list.every((step, index) => index === 0 || step.minQuantity > (list[index - 1]?.minQuantity ?? 0)),
		"List the steps by ascending quantity, each quantity once",
	);
export type QuantityDiscountList = z.infer<typeof QuantityDiscountList>;

/** The id of Consument, the fixed default group (0%, prices incl VAT, never renamed). */
export const CONSUMER_CUSTOMER_GROUP_ID = "consumer";

/** `consumer`: Consument, the one fixed default group. `business`: a group Richard made. */
export const CustomerGroupKind = z.enum(["consumer", "business"]);
export type CustomerGroupKind = z.infer<typeof CustomerGroupKind>;

/** Which side of VAT a group's prices are on: Consument incl VAT, business groups excl VAT. */
export const VatBasis = z.enum(["inclVat", "exclVat"]);
export type VatBasis = z.infer<typeof VatBasis>;

const GroupName = z.string().trim().min(1).max(100);
const Id = z.string().min(1);

// ---------------------------------------------------------------------------------------------
// Customer groups

export const CustomerGroup = z.object({
	id: z.string(),
	kind: CustomerGroupKind,
	name: z.string(),
	vatBasis: VatBasis,
	/** Below the consumer price excl VAT, on all products. Always 0 for Consument. */
	defaultDiscountPercent: z.number().int(),
	/**
	 * Approved business customers in the group (always 0 for Consument, which has no members on
	 * record). A group with customers is deleted with `deleteCustomerGroupMovingCustomers`.
	 */
	customerCount: z.number().int(),
});
export type CustomerGroup = z.infer<typeof CustomerGroup>;

/** The settings page: Consument first, then the business groups by name; and the shop's list. */
export const CustomerGroupSettings = z.object({
	groups: z.array(CustomerGroup),
	/** The quantity discount list of every product set to "Standaard van de shop". */
	shopQuantityDiscounts: z.array(QuantityDiscount),
});
export type CustomerGroupSettings = z.infer<typeof CustomerGroupSettings>;

/** A new business group. */
export const CreateCustomerGroupInput = z.object({
	name: GroupName,
	defaultDiscountPercent: DiscountPercent,
});
export type CreateCustomerGroupInput = z.infer<typeof CreateCustomerGroupInput>;

/** Changes to a business group; fields left out stay as they are. */
export const UpdateCustomerGroupBody = z.object({
	name: GroupName.optional(),
	defaultDiscountPercent: DiscountPercent.optional(),
});
export type UpdateCustomerGroupBody = z.infer<typeof UpdateCustomerGroupBody>;

/** Replaces the shop-wide list; an empty list means no quantity discount by default. */
export const ShopQuantityDiscountsBody = z.object({ quantityDiscounts: QuantityDiscountList });
export type ShopQuantityDiscountsBody = z.infer<typeof ShopQuantityDiscountsBody>;

const CustomerGroupPath = z.object({ customerGroupId: Id });

/** Where the customers of a group that is deleted go: another business group. */
export const MoveCustomersBody = z.object({ moveToCustomerGroupId: Id });
export type MoveCustomersBody = z.infer<typeof MoveCustomersBody>;

// ---------------------------------------------------------------------------------------------
// A product's pricing, as the product editor shows and edits it (`ProductContract`)

/**
 * One business group on the product's "Klantgroepen" section: the group's default (read-only here,
 * edited on the settings page) and the product's own percentage, if it deviates.
 */
export const ProductGroupDiscount = z.object({
	customerGroupId: z.string(),
	name: z.string(),
	defaultDiscountPercent: z.number().int(),
	/** "Afwijken voor dit product": the product's own percentage for every variant; `null` follows the default. */
	percent: z.number().int().nullable(),
});
export type ProductGroupDiscount = z.infer<typeof ProductGroupDiscount>;

/**
 * Which quantity discount list a product uses: `shop` ("Standaard van de shop", the list on the
 * settings page), `own` ("Eigen staffel"), or `none` ("Geen staffel": the shop's list does not
 * apply either). It applies to all variants and every customer group, Consument included.
 */
export const QuantityDiscountMode = z.enum(["shop", "own", "none"]);
export type QuantityDiscountMode = z.infer<typeof QuantityDiscountMode>;

/** A variant's own quantity discount list: it replaces whatever its product uses, for that variant. */
export const VariantQuantityDiscounts = z.object({
	variantId: z.string(),
	quantityDiscounts: z.array(QuantityDiscount),
});
export type VariantQuantityDiscounts = z.infer<typeof VariantQuantityDiscounts>;

/** The product editor's pricing sections (`ProductEditor.pricing`). */
export const ProductPricing = z.object({
	/** Every business group, by name. Consument is not listed: it is always 0%. */
	groupDiscounts: z.array(ProductGroupDiscount),
	quantityDiscountMode: QuantityDiscountMode,
	/** The product's own list. Kept while the mode is `shop` or `none`, so `own` brings it back. */
	quantityDiscounts: z.array(QuantityDiscount),
	/** The shop-wide list, read-only here: what `shop` means. */
	shopQuantityDiscounts: z.array(QuantityDiscount),
	/** The exceptions: live variants with their own list (the side panel), in no particular order. */
	variantQuantityDiscounts: z.array(VariantQuantityDiscounts),
});
export type ProductPricing = z.infer<typeof ProductPricing>;

/** The product's own percentage for one business group, or `null` to follow the group's default. */
export const GroupDiscountBody = z.object({ percent: DiscountPercent.nullable() });
export type GroupDiscountBody = z.infer<typeof GroupDiscountBody>;

/**
 * The product's quantity discount mode, and optionally its own list (replaced whole). `own` needs a
 * list with at least one step in the same body.
 */
export const QuantityDiscountsBody = z
	.object({ mode: QuantityDiscountMode, quantityDiscounts: QuantityDiscountList.optional() })
	.refine((body) => body.mode !== "own" || (body.quantityDiscounts?.length ?? 0) > 0, {
		message: "An own list needs at least one step",
		path: ["quantityDiscounts"],
	});
export type QuantityDiscountsBody = z.infer<typeof QuantityDiscountsBody>;

/** A variant's own list (at least one step), or `null` to follow the product again. */
export const VariantQuantityDiscountsBody = z.object({
	quantityDiscounts: QuantityDiscountList.refine(
		(list) => list.length > 0,
		"Give at least one step",
	).nullable(),
});
export type VariantQuantityDiscountsBody = z.infer<typeof VariantQuantityDiscountsBody>;

// ---------------------------------------------------------------------------------------------
// Price resolution

/**
 * One rule that made a price, in the order they applied (0% steps are left out): the starting point,
 * then each discount. The admin words them ("Groothandel −10% (afwijkend voor dit product) · staffel
 * vanaf 10 −10%"); the API gives them as data.
 */
export const AppliedPriceRule = z.discriminatedUnion("rule", [
	/** The customer's own fixed price (excl VAT): final, nothing else applied. */
	z.object({ rule: z.literal("customerPrice") }),
	/** Started from the variant's consumer price, on the group's basis. */
	z.object({ rule: z.literal("consumerPrice") }),
	z.object({
		rule: z.literal("groupDiscount"),
		customerGroupName: z.string(),
		percent: z.number().int(),
		/** `product`: the product's own percentage for the group ("afwijkend"); `group`: the group's default. */
		source: z.enum(["product", "group"]),
	}),
	z.object({
		rule: z.literal("quantityDiscount"),
		minQuantity: z.number().int(),
		percent: z.number().int(),
		/** Whose list: the variant's own, the product's own, or the shop-wide default. */
		source: z.enum(["variant", "product", "shop"]),
	}),
]);
export type AppliedPriceRule = z.infer<typeof AppliedPriceRule>;

/** What one unit of a variant costs a customer at a quantity, and why. */
export const ResolvedPrice = z.object({
	variantId: z.string(),
	quantity: z.number().int(),
	customerGroupId: z.string(),
	/** The basis `unitPrice` and `lineTotal` are on: Consument incl VAT, business groups excl VAT. */
	vatBasis: VatBasis,
	unitPrice: Cents,
	/** `unitPrice × quantity`. */
	lineTotal: Cents,
	/** The product's rate. What a line actually charges (reverse charge: 0%) is the order's business. */
	vatRate: z.number().int(),
	unitPriceExcl: Cents,
	unitPriceIncl: Cents,
	applied: z.array(AppliedPriceRule),
	/** A customer price: final, no quantity discount on top, and no discount code on the line. */
	final: z.boolean(),
});
export type ResolvedPrice = z.infer<typeof ResolvedPrice>;

/**
 * "What does this group, or this customer, pay for this variant at this quantity?" (Consument by
 * default). With `customerId`, the customer's own situation: their group, and their customer price
 * (final) if they have one; `customerGroupId` is then ignored.
 */
export const PricePreviewQuery = z.object({
	variantId: Id,
	quantity: z.coerce.number().int().min(1).max(1_000_000).default(1),
	customerGroupId: Id.default(CONSUMER_CUSTOMER_GROUP_ID),
	customerId: Id.optional(),
});
export type PricePreviewQuery = z.infer<typeof PricePreviewQuery>;

// ---------------------------------------------------------------------------------------------
// Customer prices (#81): a business customer's own fixed prices, on the customer's page

/** One variant the customer has a price for. */
export const CustomerPrice = z.object({
	variantId: z.string(),
	productId: z.string(),
	productName: z.string(),
	/** The value names in option order (`XXL / Blauw`); the product's name without options. */
	variantName: z.string(),
	sku: z.string(),
	/** The customer's price, excl VAT. Final. */
	priceCents: Cents,
	/** What the customer's group would pay for one, excl VAT: what the customer price replaces. */
	groupPriceCents: Cents,
	vatRate: z.number().int(),
	/** Its product is archived (spec, A2): the price stays, and cannot change until it is restored. */
	archived: z.boolean(),
});
export type CustomerPrice = z.infer<typeof CustomerPrice>;

/** The customer's prices, by product and variant name. Deleted variants are left out. */
export const CustomerPrices = z.object({
	customerId: z.string(),
	/** Whether the customer is an approved business customer: only then do the prices count. */
	allowed: z.boolean(),
	items: z.array(CustomerPrice),
});
export type CustomerPrices = z.infer<typeof CustomerPrices>;

/** A customer price excl VAT, in cents: at most €100.000. */
export const CustomerPriceBody = z.object({ priceCents: Cents.min(0).max(10_000_000) });
export type CustomerPriceBody = z.infer<typeof CustomerPriceBody>;

const CustomerPath = z.object({ customerId: Id });
const CustomerVariantPath = CustomerPath.extend({ variantId: Id });

// ---------------------------------------------------------------------------------------------
// Procedures (admin only: every one needs a signed-in employee)

export const PriceContract = {
	listCustomerGroups: oc
		.route({ method: "GET", path: "/admin/customer-groups" })
		.output(CustomerGroupSettings),
	/** Errors: `CustomerGroupNameTaken` (409). */
	createCustomerGroup: oc
		.route({ method: "POST", path: "/admin/customer-groups" })
		.input(CreateCustomerGroupInput)
		.output(CustomerGroupSettings),
	/**
	 * Errors: `CustomerGroupNotFound` (404), `CustomerGroupNameTaken` (409), `ConsumerGroupFixed`
	 * (409): Consument cannot be changed.
	 */
	updateCustomerGroup: oc
		.route({ method: "PATCH", path: "/admin/customer-groups/{customerGroupId}" })
		.input(CustomerGroupPath.extend(UpdateCustomerGroupBody.shape))
		.output(CustomerGroupSettings),
	/**
	 * Deletes a business group without customers, and the products' own discounts for it. Errors:
	 * `CustomerGroupNotFound` (404), `ConsumerGroupFixed` (409): Consument cannot be deleted,
	 * `CustomerGroupInUse` (409): the group has customers (`customerCount`), delete it with
	 * `deleteCustomerGroupMovingCustomers`; `CustomerGroupHasDiscountCodes` (409, with `codes`):
	 * discount codes are limited to the group, take it out of them first.
	 */
	deleteCustomerGroup: oc
		.route({ method: "DELETE", path: "/admin/customer-groups/{customerGroupId}" })
		.input(CustomerGroupPath)
		.output(CustomerGroupSettings),
	/**
	 * Moves the group's customers to another business group and deletes the group (with the
	 * products' own discounts for it), all at once: either all of it happens or none. Works on an
	 * empty group too. Errors: `CustomerGroupNotFound` (404, either group), `ConsumerGroupFixed`
	 * (409): Consument cannot be deleted nor take business customers, `CustomerGroupMoveInvalid`
	 * (400): the group to move to is the group being deleted, `CustomerGroupHasDiscountCodes` (409,
	 * with `codes`): discount codes are limited to the group, take it out of them first.
	 */
	deleteCustomerGroupMovingCustomers: oc
		.route({ method: "POST", path: "/admin/customer-groups/{customerGroupId}/move-customers-and-delete" })
		.input(CustomerGroupPath.extend(MoveCustomersBody.shape))
		.output(CustomerGroupSettings),
	setShopQuantityDiscounts: oc
		.route({ method: "PUT", path: "/admin/customer-groups/quantity-discounts" })
		.input(ShopQuantityDiscountsBody)
		.output(CustomerGroupSettings),
	/**
	 * The price a customer group pays for a variant at a quantity, with the rules that made it: the
	 * same resolution the cart and checkout use. Errors: `CustomerGroupNotFound` (404),
	 * `PricedVariantNotFound` (404): no such variant, or it was deleted.
	 */
	previewPrice: oc
		.route({ method: "GET", path: "/admin/prices" })
		.input(PricePreviewQuery)
		.output(ResolvedPrice),
	/** The customer's own prices. Errors: `PricedCustomerNotFound` (404). */
	listCustomerPrices: oc
		.route({ method: "GET", path: "/admin/customers/{customerId}/prices" })
		.input(CustomerPath)
		.output(CustomerPrices),
	/**
	 * Sets (or changes) the customer's price for a variant, excl VAT. Errors:
	 * `PricedCustomerNotFound` (404), `PricedVariantNotFound` (404: no such variant, or deleted),
	 * `CustomerPricesNotAllowed` (409): only an approved business customer has customer prices,
	 * `ProductArchived` (409): the variant's product is archived; restore it first.
	 */
	setCustomerPrice: oc
		.route({ method: "PUT", path: "/admin/customers/{customerId}/prices/{variantId}" })
		.input(CustomerVariantPath.extend(CustomerPriceBody.shape))
		.output(CustomerPrices),
	/**
	 * Removes the customer's price for a variant (no error when there was none). Errors:
	 * `PricedCustomerNotFound` (404), `ProductArchived` (409): the variant's product is archived.
	 */
	removeCustomerPrice: oc
		.route({ method: "DELETE", path: "/admin/customers/{customerId}/prices/{variantId}" })
		.input(CustomerVariantPath)
		.output(CustomerPrices),
};
